import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'gatsby';
import PostPreviewText from '~/components/PostPreviewText';
import chevronUp from '~/icons/chevron-up.svg';
import Image from '~/components/Dynamic/Image';
import renderLink from '~/utils/render-link';

import useBlogPostPreviews from '~/hooks/useBlogPostPreviews';

function SubNav({ subnavName = '', layout = 'default', items = [] }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'tween', duration: 0.2 }}
      className="inset-x-0 w-full border-t md:absolute border-light-grey"
      style={{ zIndex: '1', top: '100%' }}
    >
      <div className="py-5 bg-white">
        <div className="md:container xl:max-w-[1500px]">
          <div className="">
            <div className="flex flex-col md:flex-row md:-ml-6">
              {items?.map((item, i) => (
                <div
                  key={`${subnavName}-${i}`}
                  className="flex flex-col justify-center flex-auto text-black md:pl-6"
                  style={{ flex: !item.type ? `1 1 33%` : '1 1 auto' }}
                >
                  {!item.type && <DefaultSubNavItem {...item} />}
                  {item.type === 'categories' && <CategoriesSubNavItem {...item} />}
                  {item.type === 'blog-posts' && <BlogPostSubNavItem {...item} />}
                  {item.type === 'block-row' && <BlockRow {...item} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function DefaultSubNavItem({ title, url, description, icon, className }) {
  return (
    <Link
      to={url}
      className={`bg-beige mb-4 md:mb-0 md:bg-transparent p-5 xl:p-6 flex flex-col hover:bg-light-grey transition-colors duration-150 ease-in-out ${className}`}
    >
      <div className="flex flex-col items-start justify-center h-12 mb-3 w-14 md:h-20 md:w-48">
        {icon.length > 0 ? <img src={icon} className="object-contain h-full" /> : <div className="bg-[#dcdcdc] rounded-full"></div>}
      </div>

      <h3 className="mb-2 font-bold opacity-100 text-20px font-heading" dangerouslySetInnerHTML={{ __html: title }} />
      {description && <div className="opacity-75" dangerouslySetInnerHTML={{ __html: description }} />}
    </Link>
  );
}

function BlockRow({ items = [] }) {
  return (
    <div className="space-y-1 ">
      {items.map((item, i) => (
        <Link to={renderLink(item.url)} key={`block-row-${i}`} className="block w-full p-5 px-0 transition-colors duration-150 ease-in-out border-b md:bg-beige md:px-5 border-light-grey md:border-b-0 xl:p-8 xl:py-5 hover:text-blue">
          <h3
            className="mb-1 font-bold opacity-100 text-20px font-heading"
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
          {item.description && (
            <div className="opacity-100 text-13px" dangerouslySetInnerHTML={{ __html: item.description }} />
          )}
        </Link>
      ))}
    </div>
  );
}

function CategoriesSubNavItem({ categories }) {
  return (
    <div className="flex flex-col justify-center h-full text-left md:pr-8 text-16">
      {categories &&
        categories.map((category, i) => (
          <Link
            key={`header-cat-${i}`}
            to={renderLink(category.url)}
            className="flex p-2 px-4 mb-2 font-semibold bg-light-grey md:p-0 md:bg-transparent font-heading">
              {category.title}
              <img src={chevronUp} className="ml-auto transform rotate-90 md:hidden" alt="" />
            </Link>
        ))}
    </div>
  );
}

function BlogPostSubNavItem({ post }) {
  const posts = useBlogPostPreviews();
  return (
    <div className="flex -ml-6">
      {posts.slice(0, 3).map((post) => (
        <div key={post?.full_slug} className="hidden md:block md:w-1/3 pl-6">
          <Link
            to={renderLink(post.full_slug)}
            className="flex flex-col flex-auto p-1 transition-opacity duration-150 ease-in-out hover:opacity-80"
          >
            <div className="w-full mb-4 h-36 xl:h-48 bg-light-grey">
              <Image
                blok={post?.content?.previewImage}
                className="object-contain w-full h-full"
                imageClassName="h-full w-full object-contain"
              />
            </div>
            <PostPreviewText post={post} hideAuthor={true} />
          </Link>
        </div>
      ))}
    </div>
  );
}

export default SubNav;
